import { useEffect, useRef } from "react";
import { setNavigationOrigin } from "../../../redux/actions/NavigationActions";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import * as SessionStorage from "../../../../../../common-deprecated/SessionStorage";
import { sendJsonToSalesman } from "../../../../../../common-deprecated/utils";
import {
    type BuildAndBuyActionsType,
    type BuildAndBuyDispatchType,
    type BuildAndBuyStateType,
    useBnbSelector,
} from "../../../redux/store";
import useInitFinanceInsurance from "./useInitFinanceInsurance";
import { MODAL_OK } from "../../../../../../common-deprecated/utils/modalConstants";
import Navigation, { NavOrigin, NavStep } from "../../../../shared-logic/utils/Navigation";
import { getEquipmentFilterQuery } from "../../../utils/navUtils";
import { setFilterEquipment } from "../../../../../../common-deprecated/redux/actions/UtilActions";
import { useBnbOptionalLabel } from "../../../utils/labels";
import { isMasterOrSlave } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { trackStepLoadPostCustomize, trackStepLoadPreCustomize } from "../../../utils/tracking";
import { getNavigationOriginFromStorage, getSessionStorageParams } from "../../../utils/sessionStorage";
import { scrollIntoViewAndOpen } from "../../../utils/scrollUtils";
import { triggerShowEcoLabel } from "../../../../../../common-deprecated/utils/triggerShowEcoLabel";
import { triggerShowSafetyLabel } from "../../../../../../common-deprecated/utils/triggerShowSafetyLabel";
import { useDispatch, useStore } from "../../../../../../shared-logic/redux/store";

// This hook is used to initialize the build and buy flow.
const useInit = (): void => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const store = useStore<BuildAndBuyStateType, BuildAndBuyActionsType>();
    const step = useBnbSelector((state) => state.navigation.step);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const totalPriceInitialized = useBnbSelector(
        (state) => !!state.basket.total.discount || !state.commonSettings.showPrice || !state.externalPrice.enabled,
    );

    const [overlayDisclaimerContent, overlayDisclaimerTitle, overlayDisclaimerOkay] = useBnbOptionalLabel([
        "overlayDisclaimer",
        "disclaimer",
        "ok",
    ]);
    // both v1 and v2 summary containers fetch their own data now
    useInitFinanceInsurance();

    useEffect(() => {
        // Set the filterEqupmentId in the utils if present
        const { isSelected, equipmentId } = getEquipmentFilterQuery();
        if (equipmentId) {
            dispatch(setFilterEquipment(`${isSelected.toString()}:${equipmentId}`));
        }
    }, []);

    useEffect(() => {
        sendJsonToSalesman("isLoading=false", true);

        // Only for model, engine, customize step and summary
        switch (step) {
            case NavStep.Model:
            case NavStep.Engine:
            case NavStep.Customize:
            case NavStep.Summary:
                triggerShowEcoLabel();
                triggerShowSafetyLabel();
                break;
        }

        // Show the overlay disclaimer if the label is defined and it has not been show to the user yet.
        if (
            !isMasterOrSlave(commonSettings) &&
            overlayDisclaimerContent &&
            !SessionStorage.get("disclaimerOverlayShown")
        ) {
            SessionStorage.set("disclaimerOverlayShown", "true");
            dispatch(
                showModal(
                    {
                        type: MODAL_OK,
                        contentLabel: overlayDisclaimerContent,
                        titleLabel: overlayDisclaimerTitle || "",
                        okLabel: overlayDisclaimerOkay || "",
                    },
                    1,
                ),
            );
        }

        const sessionStorageParams = getSessionStorageParams(commonSettings);
        // SessionStorage will contain the navigation origin if it has been manually set by the user.
        // If set we scrollTo and clear the set value asap
        const sessionNavigationOrigin = getNavigationOriginFromStorage(sessionStorageParams);

        if ([NavOrigin.InsuranceCta, NavOrigin.FinancialCta].includes(sessionNavigationOrigin)) {
            const targetDesignation = NavOrigin.InsuranceCta === sessionNavigationOrigin ? "insurance" : "finance";
            scrollIntoViewAndOpen(dispatch, targetDesignation, true);
            // Clear Storage to leave no artifacts
            dispatch(setNavigationOrigin());
        }
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    const lastTrackedStep = useRef<NavStep | null>(null);
    useEffect(() => {
        if (step === lastTrackedStep.current) return;

        if (!Navigation.isCustomizeOnwards(step, commonSettings.brand === "lexus")) {
            trackStepLoadPreCustomize(store.getState());
            lastTrackedStep.current = step;
            return;
        }

        if (step === NavStep.Summary && totalPriceInitialized) {
            trackStepLoadPostCustomize(store.getState());
            lastTrackedStep.current = step;
            return;
        }

        if (totalPriceInitialized) {
            trackStepLoadPostCustomize(store.getState());
            lastTrackedStep.current = step;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, totalPriceInitialized, store]);
};

export default useInit;
